import seo from 'config/seo';
import { Email, X as CloseIcon } from 'kzv-icons';
import React, { useMemo, useState } from 'react';

import Button from 'components/buttons/Button';
import InputField from 'components/forms/fields/InputField';
import FacebookCircleIcon from 'components/icons/FacebookCircleIcon';
import BaseModal from 'components/modals/BaseModal';
import Article from 'models/articles/Article';
import TippyTooltip from 'components/TippyTooltip';
import { Mixpanel } from 'utils/Mixpanel';

function ArticleShareModal({ article, open, onClose }: { article: Article; open: boolean; onClose: () => void }) {
    const sharableURL = typeof window !== 'undefined' && window.location.href ? window.location.href : '';
    const [copied, setCopied] = useState(false);

    const facebookShareUrl = useMemo(() => {
        const url = new URL('https://www.facebook.com/dialog/share');
        url.searchParams.set('app_id', seo.facebook.appId);
        url.searchParams.set('href', sharableURL);
        url.searchParams.set('feature', 'share');
        url.searchParams.set('display', 'popup');
        return url.toString();
    }, [sharableURL]);

    const copySharableURL = () => {
        navigator.clipboard.writeText(sharableURL);
        setCopied(true);

        Mixpanel.track('Article share URL copied');

        setTimeout(() => {
            setCopied(false);
        }, 1500);
    };

    const handleInputClicked = () => {
        const input = document.getElementById('sharable-url');
        input?.focus();
    };

    return (
        <BaseModal open={open} handleClose={onClose}>
            <div className="p-6 pb-8 w-[440px] max-w-full relative">
                <div className="absolute top-3 right-3 md:hidden">
                    <button className="text-2xl focus:outline-none focus:ring-0" onClick={onClose}>
                        <CloseIcon strokeWidth={2} />
                    </button>
                </div>
                <h2 className="text-brand">Deli namig</h2>
                <p className="mt-2 font-medium">{article.title}</p>

                <div className="flex flex-col sm:flex-row gap-4 justify-between mt-8">
                    <Button
                        target="_blank"
                        link={facebookShareUrl}
                        styles="pl-4 pr-4"
                        variant="contained"
                        color="secondary"
                        startIcon={<FacebookCircleIcon />}
                        onClick={() => Mixpanel.track('Article share Facebook')}
                    >
                        Facebook
                    </Button>
                    <Button
                        link={`mailto:?subject=${article.title}&amp;body=${sharableURL}`}
                        styles="pl-4 pr-4"
                        variant="contained"
                        color="secondary"
                        target="_blank"
                        onClick={() => Mixpanel.track('Article share Email')}
                        startIcon={<Email />}
                    >
                        Pošlji po e-pošti
                    </Button>
                </div>

                <div className="flex mt-6 gap-2">
                    <div className="flex-1" onClick={handleInputClicked}>
                        <InputField readOnly value={sharableURL} name="sharable-url" />
                    </div>
                    <TippyTooltip
                        title={copied ? 'Povezava kopirana!' : 'Kopiraj povezavo'}
                        position="top"
                        arrow
                        arrowSize="small"
                        animation="fade"
                    >
                        <Button variant="text" color="secondary" onClick={copySharableURL}>
                            Kopiraj
                        </Button>
                    </TippyTooltip>
                </div>

                <div className="md:hidden mt-12">
                    <Button variant="text" color="secondary" onClick={onClose}>
                        Zapri
                    </Button>
                </div>
            </div>
        </BaseModal>
    );
}

export default ArticleShareModal;
